import React, { useEffect, useId, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Plus, X } from 'lucide-react';

// Version: 1.2.0 - Support for both string arrays and object arrays with value/label/color

type MultiSelectOption = string | { value: string; label: string; color?: string };

interface MultiSelectProps {
  options: MultiSelectOption[];
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  maxItems?: number;
  id?: string;
  ariaLabel?: string;
  presentation?: 'stacked' | 'inline';
  inlineTriggerLabel?: string;
}

export const MultiSelect: React.FC<MultiSelectProps> = ({ 
  options, 
  value, 
  onChange, 
  placeholder = 'Select options',
  disabled = false,
  maxItems,
  id,
  ariaLabel,
  presentation = 'stacked',
  inlineTriggerLabel
}) => {
  // Fix for React error #185 - ensure onCheckedChange handlers are present
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const optionRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const selectedButtonRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const focusFirstOption = useRef(false);
  const pendingOptionFocusIndex = useRef<number | null>(null);
  const pendingRemovalFocusIndex = useRef<number | null>(null);
  const generatedListId = useId();
  const listId = `${id ?? generatedListId}-options`;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && focusFirstOption.current) {
      focusFirstOption.current = false;
      optionRefs.current[0]?.focus();
    }
  }, [isOpen]);

  // Ensure value is always an array
  const safeValue = Array.isArray(value) ? value : [];
  const safeOptions = Array.isArray(options) ? options : [];

  // Helper functions to handle both string and object options
  const getOptionValue = (option: MultiSelectOption): string => {
    return typeof option === 'string' ? option : option.value;
  };

  const getOptionLabel = (option: MultiSelectOption): string => {
    return typeof option === 'string' ? option : option.label;
  };

  const findOptionByValue = (val: string): string => {
    const option = safeOptions.find(opt => getOptionValue(opt) === val);
    return option ? getOptionLabel(option) : val;
  };

  const getOptionColor = (option: MultiSelectOption): string | undefined => {
    return typeof option === 'object' ? option.color : undefined;
  };

  const findColorByValue = (val: string): string | undefined => {
    const option = safeOptions.find(opt => getOptionValue(opt) === val);
    return option ? getOptionColor(option) : undefined;
  };

  const toggleOption = (optionValue: string) => {
    if (disabled) {
      return;
    }
    
    const newValue = safeValue.includes(optionValue)
      ? safeValue.filter(v => v !== optionValue)
      : [...safeValue, optionValue];
    
    // Check maxItems limit
    if (maxItems && newValue.length > maxItems) {
      return;
    }
    
    onChange(newValue);
  };

  const removeOption = (optionValue: string, index: number) => {
    if (disabled) {
      return;
    }
    pendingRemovalFocusIndex.current = index;
    onChange(safeValue.filter(v => v !== optionValue));
  };

  const availableOptions = safeOptions.filter(
    option => !safeValue.includes(getOptionValue(option))
  );
  const isInline = presentation === 'inline';

  useEffect(() => {
    if (pendingRemovalFocusIndex.current === null) {
      return;
    }
    const requestedIndex = pendingRemovalFocusIndex.current;
    pendingRemovalFocusIndex.current = null;
    if (safeValue.length === 0) {
      triggerRef.current?.focus();
      return;
    }
    selectedButtonRefs.current[Math.min(requestedIndex, safeValue.length - 1)]?.focus();
  }, [safeValue.length]);

  useEffect(() => {
    if (!isOpen || pendingOptionFocusIndex.current === null) {
      return;
    }

    const requestedIndex = pendingOptionFocusIndex.current;
    pendingOptionFocusIndex.current = null;
    if (availableOptions.length === 0) {
      setIsOpen(false);
      triggerRef.current?.focus();
      return;
    }
    optionRefs.current[Math.min(requestedIndex, availableOptions.length - 1)]?.focus();
  }, [availableOptions.length, isOpen]);

  const moveOptionFocus = (index: number, direction: 1 | -1) => {
    if (availableOptions.length === 0) {
      return;
    }
    const nextIndex = (index + direction + availableOptions.length) % availableOptions.length;
    optionRefs.current[nextIndex]?.focus();
  };

  const selectOption = (optionValue: string, index: number) => {
    pendingOptionFocusIndex.current = index;
    toggleOption(optionValue);
  };

  return (
    <div
      ref={containerRef}
      className={isInline ? 'relative flex flex-wrap items-center gap-1.5' : 'space-y-2'}
    >
      {/* Selected States Display */}
      {safeValue.length > 0 && (
        <div className={isInline ? 'contents' : 'flex flex-wrap gap-1 p-2 bg-muted/50 rounded-md border'}>
          {safeValue.map((val, index) => {
            const color = findColorByValue(val);
            if (isInline) {
              return (
                <div
                  key={`selected-${index}-${val}`}
                  className="multi-select__selected-item inline-flex h-[26px] items-center gap-1.5 rounded-[7px] border px-2.5 text-xs font-semibold"
                >
                  <span>{findOptionByValue(val)}</span>
                  {!disabled && (
                    <button
                      ref={(element) => {
                        selectedButtonRefs.current[index] = element;
                      }}
                      type="button"
                      className="multi-select__remove rounded-sm hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      aria-label={`Remove ${findOptionByValue(val)}`}
                      onClick={(event) => {
                        event.stopPropagation();
                        removeOption(val, index);
                      }}
                    >
                      <X className="h-3 w-3" aria-hidden="true" />
                    </button>
                  )}
                </div>
              );
            }
            // Use div instead of Badge when we have custom colors to avoid style conflicts
            return color ? (
              <div
                key={`selected-${index}-${val}`}
                className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors ${color}`}
              >
                <span>{findOptionByValue(val)}</span>
                {!disabled && (
                  <button
                    ref={(element) => {
                      selectedButtonRefs.current[index] = element;
                    }}
                    type="button"
                    className="ml-1 rounded-sm hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    aria-label={`Remove ${findOptionByValue(val)}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      removeOption(val, index);
                    }}
                  >
                    <X className="h-3 w-3" aria-hidden="true" />
                  </button>
                )}
              </div>
            ) : (
              <Badge
                key={`selected-${index}-${val}`}
                variant="secondary"
                className="text-xs flex items-center gap-1 transition-colors"
              >
                {findOptionByValue(val)}
                {!disabled && (
                  <button
                    ref={(element) => {
                      selectedButtonRefs.current[index] = element;
                    }}
                    type="button"
                    className="rounded-sm hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    aria-label={`Remove ${findOptionByValue(val)}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      removeOption(val, index);
                    }}
                  >
                    <X className="h-3 w-3" aria-hidden="true" />
                  </button>
                )}
              </Badge>
            );
          })}
        </div>
      )}
      
      {/* Dropdown Button */}
      <div className={isInline ? 'static inline-flex' : 'relative'}>
        <Button
          ref={triggerRef}
          type="button"
          id={id}
          aria-label={ariaLabel}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          aria-controls={isOpen ? listId : undefined}
          variant="outline"
          className={isInline
            ? 'multi-select__inline-trigger h-[26px] w-auto justify-center gap-1 rounded-[7px] border-dashed bg-transparent px-2.5 text-xs font-semibold shadow-none hover:bg-transparent'
            : 'w-full justify-between'}
          onClick={() => !disabled && setIsOpen(!isOpen)}
          onKeyDown={(event) => {
            if (event.key === 'Escape' && isOpen) {
              event.preventDefault();
              setIsOpen(false);
              triggerRef.current?.focus();
            } else if ((event.key === 'ArrowDown' || event.key === 'ArrowUp') && !disabled) {
              event.preventDefault();
              focusFirstOption.current = true;
              setIsOpen(true);
            }
          }}
          disabled={disabled}
        >
          {isInline ? (
            <>
              <Plus className="h-3 w-3" aria-hidden="true" />
              <span>{inlineTriggerLabel ?? placeholder}</span>
            </>
          ) : (
            <>
              <span className="truncate">
                {safeValue.length > 0 ? `${safeValue.length} selected` : placeholder}
              </span>
              <span className="ml-2">▼</span>
            </>
          )}
        </Button>
        {isOpen && !disabled && (
          <div
            id={listId}
            role="listbox"
            aria-label={ariaLabel ?? placeholder}
            aria-multiselectable="true"
            className={isInline
              ? 'absolute left-0 top-full z-10 mt-1 max-h-60 min-w-64 max-w-[calc(100vw-2rem)] overflow-auto rounded-md border border-border bg-background shadow-lg'
              : 'absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md border border-border bg-background shadow-lg'}
          >
            {availableOptions.map((option, index) => {
              const optionValue = getOptionValue(option);
              const optionLabel = getOptionLabel(option);
              const optionColor = getOptionColor(option);
              return (
                <button
                  ref={(element) => {
                    optionRefs.current[index] = element;
                  }}
                  type="button"
                  role="option"
                  aria-selected="false"
                  key={`option-${index}-${optionValue}`}
                  className="flex w-full items-center px-3 py-2 text-left hover:bg-muted focus-visible:bg-muted focus-visible:outline-none"
                  onClick={() => selectOption(optionValue, index)}
                  onKeyDown={(event) => {
                    if (event.key === 'ArrowDown') {
                      event.preventDefault();
                      moveOptionFocus(index, 1);
                    } else if (event.key === 'ArrowUp') {
                      event.preventDefault();
                      moveOptionFocus(index, -1);
                    } else if (event.key === 'Escape') {
                      event.preventDefault();
                      setIsOpen(false);
                      triggerRef.current?.focus();
                    }
                  }}
                >
                  <span
                    className="mr-2 h-4 w-4 shrink-0 rounded-sm border border-primary shadow"
                    aria-hidden="true"
                  />
                  <span className={optionColor ? `px-2 py-0.5 rounded-sm ${optionColor}` : ''}>
                    {optionLabel}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
